"use client";
import {forwardRef, useCallback} from "react";

// --- Hooks ---
import {useTiptapEditor} from "components/RichEditor/hooks/use-tiptap-editor";
import {useFormField} from "./use-form-field";

// --- UI ---
import {Button} from "components/RichEditor/components/tiptap-ui-primitive/button";

// --- Icon ---
import {FormInputIcon} from "components/RichEditor/components/tiptap-icons/form-input-icon";

export const FormFieldButton = forwardRef(
  (
    {
      editor: providedEditor,
      hideWhenUnavailable = false,
      onInserted,
      onClick,
      text,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const {editor} = useTiptapEditor(providedEditor);

    const {isVisible, handleInsert, canInsert, isActive, label} = useFormField({
      editor,
      hideWhenUnavailable,
      onInserted,
    });

    const handleClick = useCallback(
      (event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        handleInsert();
      },
      [handleInsert, onClick]
    );

    if (!isVisible) return null;

    return (
      <Button
        type="button"
        disabled={!canInsert}
        variant="ghost"
        data-active-state={isActive ? "on" : "off"}
        data-disabled={!canInsert}
        role="button"
        tabIndex={-1}
        aria-label={label}
        aria-pressed={isActive}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <FormInputIcon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </Button>
    );
  }
);

FormFieldButton.displayName = "FormFieldButton";
